import React, { useState, useEffect, useMemo } from "react";
import { Container, Form, Checkbox, Dropdown } from "semantic-ui-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import useNamedDebouncedCallback from "../../hooks/useNamedDebouncedCallback";
import toBase64 from "../../utils/toBase64";
import DatePicker, { registerLocale } from "react-datepicker";
import { sendFormApi, getProductsApi } from "../../api";
import { usePolyglot } from "ceco-polyglot";
import format from "date-fns/format";
import de from "date-fns/locale/de";
import en from "date-fns/locale/en-US";
import es from "date-fns/locale/es";
import fr from "date-fns/locale/fr";
import it from "date-fns/locale/it";
import pt from "date-fns/locale/pt";
import "./FormContact.scss";
import CecoCareModal from "../CecoCareModal/CecoCareModal";
import messageFactory from "../../modules/message-factory/message-factory";
import { useAlert } from "../../context/AlertProvider";
import { COUNTRIES } from "../../constants/constants";
import provincesCountry from "../../modules/provinces-country/provinces-country";

registerLocale("de", de);
registerLocale("en", en);
registerLocale("es", es);
registerLocale("fr", fr);
registerLocale("it", it);
registerLocale("pt", pt);

const CECOCARE_MODAL_OPENER_OPTIONS = ['sat-request', 'sat-status'];

export default function FormContact(props) {
  const alert = useAlert();
  const [cecoCarePopupIsVisible, setCecoCareIsVisible] = useState(false);
  const [country, setCountry] = useState("");
  const [provinces, setProvinces] = useState([]);
  const [countries, setCountries] = useState([]);
  const [hasCecoCareOptionSelected, setHasCecoCareOptionSelected] =
    useState(false);
  const polyglot = usePolyglot();
  const t = polyglot.t.bind(polyglot);
  const [loading, setLoading] = useState(false);
  const typeManagementList = useMemo(
    () => [
      {
        key: 0,
        text: t("FormContact.sat-request"),
        value: "sat-request"
      },
      {
        key: 1,
        text: t("FormContact.sat-status"),
        value: "sat-status"
      },
      {
        key: 2,
        text: t("FormContact.spare-parts-request"),
        value: "spare-parts-request"
      },
      {
        key: 3,
        text: t("FormContact.doa-change"),
        value: "doa-change"
      },
      {
        key: 6,
        text: t("FormContact.damaged-delivery"),
        value: "damaged-delivery"
      }
    ],
    [t]
  );

  const { setIdSac, setResponseForm } = props;
  const [errorMessage, setErrorMessage] = useState("");
  const [checkCondition, setCheckCondition] = useState(false);
  const [products, setProducts] = useState([]);
  const [validationFile, setValidationFile] = useState(true);

  useEffect(() => {
    setCountries(
      COUNTRIES.map(
        (country) => ({
          key: country.value,
          text: country.name,
          value: country.value
        }),
        []
      )
    );

    getProductsApi().then((response) => {
      if (!response.error) {
        const productArray = [];
        response.data.forEach((product, index) => {
          productArray.push({
            key: index,
            text: `${product.sku} - ${product.description}`,
            value: `${product.sku} - ${product.description}`
          });
        });
        setProducts(productArray);
      }
    });
  }, []);

  useEffect(() => {
    const provinces = provincesCountry(country);
    setProvinces(provinces);
  }, [country]);
  const formik = useFormik({
    initialValues: defaultValue(),
    validationSchema: validationSchema(),
    onSubmit: async (formValue) => {
      setLoading(!loading);
      const dateFormat = format(formValue.purchase_date, "dd/MM/yyyy");

      const data = {
        ...formValue,
        purchase_date: dateFormat,
        locale: polyglot.currentLocale
      };

      if (formValue.images) {
        data.images = await Promise.all(
          [...formValue.images].map((image) => toBase64(image))
        );
      }

      sendFormApi(data)
        .then((response) => {
          if (response.success) {
            setLoading(false);
            formik.handleReset();
            const expireCookie = new Date();
            expireCookie.setMinutes(expireCookie.getMinutes() + 5);
            document.cookie = `new_form=true;expires=${expireCookie}`;
            localStorage.setItem("idSac", response.id_sac);
            localStorage.setItem("response", response);

            const date = new Date();
            const currentTime = date.getTime();
            localStorage.setItem("currentDate", currentTime);

            setResponseForm(response);
            setIdSac(response.id_sac);

            localStorage.clear();
          } else {
            alert.current.error(
              `${t(messageFactory(response))} ${
                !!response.payload?.id_sac
                  ? "ID SAC: ".concat(response.payload?.id_sac)
                  : ""
              } `
            );
            setErrorMessage(t(messageFactory(response)));
            setLoading(false);
          }
        })
        .catch(() => {
          setErrorMessage(t("error.send"));
        });
    }
  });

  const onDropFile = (files) => {
    let allFileCorrect = true;

    for (var i = 0; i < files.length; i++) {
      if (
        files[i].type !== "image/jpeg" &&
        files[i].type !== "application/pdf" &&
        files[i].type !== "image/png" &&
        files[i].type !== "image/jpg"
      ) {
        allFileCorrect = false;
      }
    }

    if (allFileCorrect) {
      setErrorMessage(null);
      setValidationFile(true);
      formik.setFieldValue("images", files);
    } else {
      setErrorMessage(t("error.invalid-files"));
      setValidationFile(false);
      setCheckCondition(false);
    }
  };
  const setForm = useNamedDebouncedCallback((name, value) => {
    formik.setFieldValue(name, value);
  }, 300);

  return (
    <>
      <Container text className="form-contact">
        <h1>{t("FormContact.title")}</h1>
        <div className="info">
          <p>{t("FormContact.prompt")}</p>
        </div>
        <Form onSubmit={formik.handleSubmit} autoComplete="off">
          <Form.Group widths="equal">
            <Form.Select
              fluid
              name="sac_type"
              options={typeManagementList}
              placeholder={`${t("field.sac_type")}*`}
              onChange={(_, data) => {
                const optionIncludesCecocare =
                  CECOCARE_MODAL_OPENER_OPTIONS.includes(data.value);
                setHasCecoCareOptionSelected(optionIncludesCecocare);
                setCecoCareIsVisible(optionIncludesCecocare);

                formik.setFieldValue(data.name, data.value);
              }}
              error={!!formik.touched.sac_type && !!formik.errors.sac_type}
            />
          </Form.Group>
          <Form.Group>
            <Form.Input
              fluid
              type="text"
              placeholder={`${t("field.first_name")}*`}
              name="first_name"
              onChange={(e) => setForm(e.target.name, e.target.value)}
              error={!!formik.touched.first_name && !!formik.errors.first_name}
              width={8}
              autoComplete="new-name"
            />
            <Form.Input
              fluid
              type="text"
              placeholder={`${t("field.last_name")}*`}
              name="last_name"
              onChange={(e) => setForm(e.target.name, e.target.value)}
              error={!!formik.touched.last_name && !!formik.errors.last_name}
              width={8}
              autoComplete="new-name"
            />
          </Form.Group>
          <Form.Group>
            <Form.Input
                fluid
                type="text"
                name="nif"
                placeholder={`${t("field.nif")}*`}
                onChange={(e) => setForm(e.target.name, e.target.value)}
                error={!!formik.touched.nif && !!formik.errors.nif}
                width={4}
                autoComplete="new-nif"
            />
            <Form.Input
              fluid
              type="email"
              name="email"
              placeholder={`${t("field.email")}*`}
              onChange={(e) => setForm(e.target.name, e.target.value)}
              error={!!formik.touched.email && !!formik.errors.email}
              width={8}
              autoComplete="new-email"
            />
            <Form.Input
              fluid
              type="tel"
              name="phone"
              placeholder={`${t("field.phone")}*`}
              onChange={(e) => setForm(e.target.name, e.target.value)}
              error={!!formik.touched.phone && !!formik.errors.phone}
              width={4}
              autoComplete="new-phone"
            />
          </Form.Group>
          <Form.Group widths="equal">
            <div className="wide field">
              <Dropdown
                fluid
                name="product"
                options={products}
                placeholder={`${t("field.product")}*`}
                search
                selection
                lazyLoad
                onChange={(_, data) =>
                  formik.setFieldValue(data.name, data.value)
                }
                error={!!formik.touched.product && !!formik.errors.product}
              />
            </div>
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Input
              fluid
              type="text"
              name="serial_number"
              placeholder={`${t("field.serial_number")}*`}
              onChange={(e) => setForm(e.target.name, e.target.value)}
              error={
                !!formik.touched.serial_number && !!formik.errors.serial_number
              }
              autoComplete="new-serialNumber"
            />
            <Form.Input
              fluid
              type="text"
              name="bill_number"
              placeholder={`${t("field.bill_number")}*`}
              onChange={(e) => setForm(e.target.name, e.target.value)}
              error={
                !!formik.touched.bill_number && !!formik.errors.bill_number
              }
              autoComplete="new-billNumber"
            />
          </Form.Group>
          <Form.Group widths="equal">
            <div
              className={
                "wide field" +
                (formik.touched.purchase_date && formik.errors.purchase_date
                  ? " error"
                  : "")
              }
            >
              <DatePicker
                fluid
                placeholderText={`${t("field.purchase_date")}*`}
                locale={polyglot.currentLocale}
                selected={
                  formik.values.purchase_date === ""
                    ? null
                    : formik.values.purchase_date
                }
                onChange={(e) => formik.setFieldValue("purchase_date", e)}
                dateFormat="dd/MM/yyyy"
              />
            </div>
            <Form.Input
              fluid
              type="text"
              placeholder={t("field.previous_rma")}
              name="previous_rma"
              onChange={(e) => setForm(e.target.name, e.target.value)}
              error={
                !!formik.touched.previous_rma && !!formik.errors.previous_rma
              }
              autoComplete="new-rma"
            />
          </Form.Group>
          <Form.Group>
            <Form.Input
              fluid
              type="text"
              name="address"
              placeholder={`${t("field.address")}*`}
              onChange={(e) => setForm(e.target.name, e.target.value)}
              error={!!formik.touched.address && !!formik.errors.address}
              width={8}
              autoComplete="new-address"
            />
            <Form.Input
              fluid
              type="text"
              name="number"
              placeholder={`${t("field.number")}*`}
              onChange={(e) => setForm(e.target.name, e.target.value)}
              error={!!formik.touched.number && !!formik.errors.number}
              width={2}
              autoComplete="new-number"
            />
            <Form.Input
              fluid
              type="text"
              name="floor"
              placeholder={t("field.floor")}
              onChange={(e) => setForm(e.target.name, e.target.value)}
              error={!!formik.touched.floor && !!formik.errors.floor}
              width={3}
              autoComplete="new-floor"
            />
            <Form.Input
              fluid
              type="text"
              name="door"
              placeholder={t("field.door")}
              onChange={(e) => setForm(e.target.name, e.target.value)}
              error={!!formik.touched.door && !!formik.errors.door}
              width={3}
              autoComplete="new-door"
            />
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Dropdown
              fluid
              search
              name="country"
              placeholder={`${t("field.country")}*`}
              onChange={(_, data) => {
                formik.setFieldValue(data.name, data.value);
                setCountry(data.value);
              }}
              error={!!formik.touched.country && !!formik.errors.country}
              autoComplete="new-country"
              options={countries}
              selection
            />
            <Form.Dropdown
              fluid
              search
              placeholder={`${t("field.province")}*`}
              name="province"
              onChange={(_, data) =>
                formik.setFieldValue(data.name, data.value)
              }
              error={!!formik.touched.province && !!formik.errors.province}
              autoComplete="new-province"
              options={provinces || [{ key: "", value: "", text: "" }]}
              selection
            />
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Input
              fluid
              type="text"
              name="town"
              placeholder={`${t("field.town")}*`}
              onChange={(e) => setForm(e.target.name, e.target.value)}
              error={!!formik.touched.town && !!formik.errors.town}
              autoComplete="new-town"
            />
            <Form.Input
              fluid
              type="text"
              name="postal_code"
              placeholder={`${t("field.postal_code")}*`}
              onChange={(e) => setForm(e.target.name, e.target.value)}
              error={
                !!formik.touched.postal_code && !!formik.errors.postal_code
              }
              autoComplete="new-postalcode"
            />
          </Form.Group>
          <Form.Group widths="equal">
            <Form.TextArea
              error={
                !!formik.touched.cause_of_contact &&
                !!formik.errors.cause_of_contact
              }
              placeholder={`${t("field.cause_of_contact")}*`}
              name="cause_of_contact"
              rows={6}
              onChange={(e) => setForm(e.target.name, e.target.value)}
              autoComplete="new-cause"
            />
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Input
              name="images"
              type="file"
              error={
                formik.touched.images &&
                formik.errors.images &&
                formik.errors.images
              }
              fluid
              multiple
              accept=".png,.jpg,.jpeg,.pdf"
              onChange={(e) => {
                onDropFile(e.target.files);
              }}
            />
          </Form.Group>
          <p className="alert">{t("field.obligatory_warning")}</p>
          <div className="condition">
            <Checkbox
              checked={checkCondition}
              onClick={(e, data) => setCheckCondition(data.checked)}
            />
            <p onClick={() => setCheckCondition(!checkCondition)}>
              {t("field.privacy_policy_prompt")}{" "}
              <a href={t("field.privacy_policy_link")}>
                {t("field.privacy_policy")}
              </a>
            </p>
          </div>
          {!validationFile ? null : (
            <Form.Button
              type="submit"
              loading={loading}
              disabled={!checkCondition || hasCecoCareOptionSelected}
            >
              {t("action.send")}
            </Form.Button>
          )}
        </Form>
        <p className="error">{ formik.errors && formik.submitCount > 0 ? "Debe rellenar todos los campos obligatorios" : errorMessage}</p>
      </Container>
      <CecoCareModal
        cecoCarePopupIsVisible={cecoCarePopupIsVisible}
        setCecoCareIsVisible={setCecoCareIsVisible}
      />
    </>
  );
}

function defaultValue() {
  return {
    first_name: "",
    last_name: "",
    nif: "",
    email: "",
    phone: "",
    product: "",
    serial_number: "",
    bill_number: "",
    purchase_date: "",
    sac_type: "",
    address: "",
    number: "",
    floor: "",
    door: "",
    country: "",
    province: "",
    town: "",
    postal_code: "",
    cause_of_contact: "",
    previous_rma: "",
    images: undefined
  };
}

function validationSchema() {
  return Yup.object({
    sac_type: Yup.string().required().notOneOf(CECOCARE_MODAL_OPENER_OPTIONS),
    first_name: Yup.string().required(),
    last_name: Yup.string().required(),
    nif: Yup.string().required(),
    email: Yup.string().email("Email incorrecto").required(),
    phone: Yup.string().required(),
    product: Yup.string().required(),
    serial_number: Yup.string().required(),
    bill_number: Yup.string().required(),
    purchase_date: Yup.string().required(),
    previous_rma: Yup.string(),
    address: Yup.string().required(),
    number: Yup.string().required(),
    floor: Yup.string(),
    door: Yup.string(),
    country: Yup.string().required(),
    province: Yup.string().required(),
    town: Yup.string().required(),
    postal_code: Yup.string().required(),
    cause_of_contact: Yup.string().required(),
    images: Yup.mixed()
      .test("FileList", "error.invalid-files", (value) => {
        return value === undefined || value instanceof FileList;
      })
      .test("amount", "error.too-many-files", (value) => {
        return value === undefined || (value.length > 0 && value.length < 4);
      })
      .test("size", "error.file-to-big", (value) => {
        if (value) {
          const totalSize = [...value].reduce(
            (size, image) => size + image.size,
            0
          );
          return totalSize < 6291456; //6MB
        }
        return value === undefined;
      })
      .required()
  });
}
